/**
 * clerk-auth.js
 *
 * Initializes Clerk's SignIn or SignUp component on custom Shopify auth pages.
 * Configured via data-* attributes on #clerk-auth-container.
 *
 * Required attributes:
 *   data-publishable-key  — Clerk publishable key (pk_live_... or pk_test_...)
 *   data-mode             — "sign-in" | "sign-up"
 *
 * Optional attributes:
 *   data-after-sign-in-url  — redirect URL after sign-in (default: /account)
 *   data-after-sign-up-url  — redirect URL after sign-up (default: /account)
 *
 * Auth flow:
 *   1. Shopify OIDC IDP flow redirects to Clerk /oauth/authorize
 *   2. Clerk checks for an active session; if none, redirects to this page
 *   3. User authenticates here via Clerk's SignIn/SignUp component
 *   4. Clerk completes the pending OAuth request → redirects to Shopify callback
 *   5. Shopify creates a customer session → {{ customer }} is populated
 *
 * https://clerk.com/docs/js-frontend/getting-started/quickstart
 *
 * Note: clerk.browser.js is the no-bundler CDN build. It self-initializes a
 * global `window.Clerk` by reading `data-clerk-publishable-key` off its own
 * <script> tag, so it must be added as a real <script> tag (not dynamically
 * import()'d) or it initializes with an empty key and throws.
 */
(function clerkAuth() {
  'use strict';

  const container = document.getElementById('clerk-auth-container');
  if (!container) return;

  const publishableKey = container.getAttribute("data-publishable-key");
  const mode = container.getAttribute('data-mode');
  // A `redirect_url` param means Clerk sent us here to resume a pending
  // Shopify OAuth request (to its hosted consent screen).
  // clerk.Window.mountSignIn()/mountSignUp() doesn't pick this up automatically, so it must win over our own default.
  const pendingOAuthContinuationUrl = new URLSearchParams(window.location.search).get('redirect_url');
  const afterSignInUrl = pendingOAuthContinuationUrl || container.getAttribute('data-after-sign-in-url') || '/account';
  const afterSignUpUrl = pendingOAuthContinuationUrl || container.getAttribute('data-after-sign-up-url') || '/account';
  const termsUrl = container.getAttribute('data-terms-url');
  const privacyUrl = container.getAttribute('data-privacy-url');

  if (!publishableKey) {
    console.error(
      '[Clerk] Missing publishable key. Add it in Theme Settings → Clerk.'
    );
    return;
  }

  // Derive the Clerk Frontend API domain from the publishable key.
  // The key is base64-url-encoded and contains the domain in the third segment.
  let clerkDomain;
  try {
    clerkDomain = atob(publishableKey.split('_')[2]).slice(0, -1);
  } catch (e) {
    console.error('[Clerk] Invalid publishable key format.', e);
    return;
  }

  const uiScript = document.createElement('script');
  uiScript.src = `https://${clerkDomain}/npm/@clerk/ui@1/dist/ui.browser.js`;
  uiScript.async = true;
  uiScript.crossOrigin = 'anonymous';
  // Both scripts are async, so the UI bundle isn't guaranteed to have set
  // window.__internal_ClerkUICtor by the time clerk.browser.js loads.
  const uiReady = new Promise((resolve, reject) => {
    uiScript.onload = resolve;
    uiScript.onerror = () => reject(new Error('Failed to load @clerk/ui bundle.'));
  });

  const clerkScript = document.createElement('script');
  // Pinned to the major version (not @latest, which resolves to a stale
  // pre-Core-2 build on this CDN path and is missing newer features like
  // legal consent).
  clerkScript.src = `https://${clerkDomain}/npm/@clerk/clerk-js@6.35.0/dist/clerk.browser.js`;
  clerkScript.async = true;
  clerkScript.crossOrigin = 'anonymous';
  // Read by clerk.browser.js's own auto-init logic once it loads.
  clerkScript.setAttribute('data-clerk-publishable-key', publishableKey);

  // Structural options only; visual styling lives in component-clerk-auth.css.
  const appearance = {
    layout: {
      socialButtonsPlacement: "bottom",
      socialButtonsVariant: "iconButton",
      // Without both URLs Clerk won't render the legal-consent checkbox, so
      // "legal_accepted" (required by this instance) can never be satisfied.
      termsPageUrl: termsUrl,
      privacyPageUrl: privacyUrl,
      showOptionalFields: true,
    },
    // Clerk's docs list these under `options` rather than `layout`; set both
    // since this SDK version has been observed reading from `layout`.
    options: {
      socialButtonsPlacement: "bottom",
      socialButtonsVariant: "iconButton",
      termsPageUrl: termsUrl,
      privacyPageUrl: privacyUrl,
      showOptionalFields: true,
    },
    // variables: {
    //   colorPrimary: "#494949"
    // }
  };

  clerkScript.onload = async function () {
    try {
      await uiReady;
    } catch (e) {
      console.error('[Clerk]', e.message);
      return;
    }

    await window.Clerk.load({
      ui: { ClerkUI: window.__internal_ClerkUICtor },
    });

    // Clerk redirects to the Home URL (not redirect_url) when a signed-in user
    // hits a sign-in/up component, which would drop the pending OAuth request.
    if (window.Clerk.user) {
      window.location.href = afterSignInUrl;
      return;
    }

    const mount = document.getElementById('clerk-auth-mount');
    if (!mount) {
      console.error('[Clerk] Missing #clerk-auth-mount element.');
      return;
    }

    if (mode === 'sign-up') {
      window.Clerk.mountSignUp(mount, {
        appearance: appearance,
        // forceRedirectUrl wins unconditionally, so only set it when resuming a
        // pending Shopify OAuth request; otherwise fall back to the default.
        forceRedirectUrl: pendingOAuthContinuationUrl || undefined,
        fallbackRedirectUrl: afterSignUpUrl,
        signInUrl: '/pages/sign-in',
      });
    } else {
      window.Clerk.mountSignIn(mount, {
        appearance: appearance,
        forceRedirectUrl: pendingOAuthContinuationUrl || undefined,
        fallbackRedirectUrl: afterSignInUrl,
        signUpUrl: '/pages/sign-up',
      });
    }
  };
  clerkScript.onerror = function () {
    console.error('[Clerk] Failed to load clerk.browser.js from CDN.');
  };

  document.head.appendChild(uiScript);
  document.head.appendChild(clerkScript);
})();
